<!--
  Dipendente del partner — dettaglio, sul modello delle schede cliente e
  partner.

  Cose non ovvie:
  · LE SEDI DECIDONO IL PARTNER. Il backend ricava `employee.partnerId` dalle
    sedi assegnate (regola «1 account = 1 partner»): togliendole tutte il
    dipendente resta senza partner e non vede più niente. Per questo almeno
    una sede è obbligatoria.
  · L'EMAIL È L'ACCESSO al gestionale partner: cambiarla cambia il login.
  · La password non si gestisce da qui: il dipendente la reimposta dal login.
-->
<template>
  <div
    v-loading="loading"
    class="edt"
  >
    <div class="edt__head">
      <button
        type="button"
        class="edt__back"
        aria-label="Torna al partner"
        @click="goBack"
      >
        ‹
      </button>
      <span
        class="edt__avatar"
        aria-hidden="true"
      >{{ initials }}</span>
      <div>
        <h1 class="edt__title">
          {{ fullName }}
          <span class="edt__role">{{ roleLabel(employee.role) }}</span>
        </h1>
        <div class="edt__sub">
          {{ [employee.mail, employee.telephone_number].filter(Boolean).join(' · ') || '—' }}
        </div>
      </div>
      <div class="edt__spacer" />
      <div
        v-if="canEdit && loaded"
        class="edt__actions"
      >
        <template v-if="!editing">
          <el-button
            type="danger"
            plain
            :loading="deleting"
            @click="remove"
          >
            Elimina
          </el-button>
          <el-button @click="startEdit">
            Modifica
          </el-button>
        </template>
        <template v-else>
          <el-button @click="cancelEdit">
            Annulla
          </el-button>
          <el-button
            type="primary"
            :loading="saving"
            @click="save"
          >
            Salva modifiche
          </el-button>
        </template>
      </div>
    </div>

    <div
      v-if="employee.is_deleted"
      class="edt__banner"
      role="status"
    >
      Questo dipendente è stato eliminato: non entra più nel gestionale.
    </div>

    <div
      v-if="loadError"
      class="edt__banner"
      role="alert"
    >
      Non riesco a leggere il dipendente.
      <el-button
        size="small"
        @click="load"
      >
        Riprova
      </el-button>
    </div>

    <div
      v-if="loaded"
      class="edt__grid"
    >
      <div class="edt__col">
        <!-- ------------------------------- dati ------------------------------ -->
        <section class="edt__card">
          <header class="edt__cardHead">
            <h3>Dati del dipendente</h3>
          </header>
          <div class="edt__cardBody">
            <div class="edt__fields">
              <div class="edt__field">
                <label
                  class="edt__label"
                  for="edt-name"
                >Nome</label>
                <el-input
                  v-if="editing"
                  id="edt-name"
                  v-model="draft.name"
                />
                <span
                  v-else
                  class="edt__value"
                >{{ employee.name || '—' }}</span>
              </div>
              <div class="edt__field">
                <label
                  class="edt__label"
                  for="edt-surname"
                >Cognome</label>
                <el-input
                  v-if="editing"
                  id="edt-surname"
                  v-model="draft.surname"
                />
                <span
                  v-else
                  class="edt__value"
                >{{ employee.surname || '—' }}</span>
              </div>
              <div class="edt__field">
                <label
                  class="edt__label"
                  for="edt-mail"
                >Email</label>
                <el-input
                  v-if="editing"
                  id="edt-mail"
                  v-model="draft.mail"
                  autocomplete="off"
                />
                <span
                  v-else
                  class="edt__value"
                >{{ employee.mail || '—' }}</span>
                <span
                  v-if="editing"
                  class="edt__help"
                >È l'accesso al gestionale partner: cambiandola cambia il login.</span>
              </div>
              <div class="edt__field">
                <label
                  class="edt__label"
                  for="edt-phone"
                >Telefono</label>
                <el-input
                  v-if="editing"
                  id="edt-phone"
                  v-model="draft.telephone_number"
                  autocomplete="off"
                />
                <span
                  v-else
                  class="edt__value"
                >{{ employee.telephone_number || '—' }}</span>
              </div>
              <div class="edt__field edt__field--full">
                <span class="edt__label">Ruolo</span>
                <div
                  v-if="editing"
                  class="edt__roles"
                  role="radiogroup"
                  aria-label="Ruolo"
                >
                  <button
                    v-for="r in ROLES"
                    :key="r.value"
                    type="button"
                    role="radio"
                    class="edt__roleOpt"
                    :class="{ 'is-on': draft.role === r.value }"
                    :aria-checked="draft.role === r.value"
                    @click="draft.role = r.value"
                  >
                    <span class="edt__roleName">{{ r.label }}</span>
                    <span class="edt__roleHint">{{ r.hint }}</span>
                  </button>
                </div>
                <span
                  v-else
                  class="edt__value"
                >{{ roleLabel(employee.role) }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- ------------------------------- sedi ------------------------------ -->
        <section class="edt__card">
          <header class="edt__cardHead">
            <h3>Sedi</h3>
            <span class="edt__count">{{ editing ? draft.headquarterIds.length : assigned.length }}</span>
          </header>
          <div class="edt__cardBody">
            <template v-if="editing">
              <p
                v-if="!partnerHeadquarters.length"
                class="edt__empty"
              >
                Il partner non ha sedi.
              </p>
              <el-checkbox-group
                v-else
                v-model="draft.headquarterIds"
                class="edt__hqList"
              >
                <el-checkbox
                  v-for="h in partnerHeadquarters"
                  :key="h.id"
                  :label="h.id"
                  class="edt__hqCheck"
                >
                  <span class="edt__hqName">{{ h.name || h.city || 'Sede' }}</span>
                  <span class="edt__hqAddr">{{ addressOf(h) }}</span>
                </el-checkbox>
              </el-checkbox-group>
              <p
                v-if="!draft.headquarterIds.length"
                class="edt__warn"
              >
                Serve almeno una sede: è dalle sedi che il dipendente prende il suo partner.
              </p>
            </template>
            <template v-else>
              <p
                v-if="!assigned.length"
                class="edt__warn"
              >
                Nessuna sede assegnata: il dipendente non è collegato a nessun partner e non vede
                niente nel gestionale partner.
              </p>
              <div
                v-for="h in assigned"
                :key="h.id"
                class="edt__hq"
              >
                <span class="edt__hqName">{{ h.name || h.city || 'Sede' }}</span>
                <span class="edt__hqAddr">{{ addressOf(h) }}</span>
              </div>
            </template>
          </div>
        </section>
      </div>

      <!-- ------------------------------ riepilogo ---------------------------- -->
      <aside class="edt__aside">
        <section class="edt__card">
          <header class="edt__cardHead">
            <h3>Riepilogo</h3>
          </header>
          <div class="edt__cardBody">
            <div class="edt__kv">
              <span>Partner</span>
              <router-link
                v-if="partner.id"
                :to="`/partner/${partner.id}`"
                class="edt__link"
              >
                {{ partner.company_name || 'Partner' }}
              </router-link>
              <b v-else>—</b>
            </div>
            <div class="edt__kv">
              <span>Ruolo</span><b>{{ roleLabel(employee.role) }}</b>
            </div>
            <div class="edt__kv">
              <span>Sedi assegnate</span><b>{{ assigned.length }} di {{ partnerHeadquarters.length }}</b>
            </div>
          </div>
        </section>

        <section class="edt__card">
          <header class="edt__cardHead">
            <h3>Nel gestionale partner vede</h3>
          </header>
          <div class="edt__cardBody">
            <div class="edt__menu">
              <span
                v-for="v in menuFor(editing ? draft.role : employee.role)"
                :key="v"
                class="edt__menuItem"
              >{{ v }}</span>
            </div>
            <p class="edt__note">
              Dipendente e venditore vedono le stesse pagine; l'amministratore in più le
              Tariffe. Le voci legate ai servizi compaiono solo se il partner li ha.
            </p>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import partnerAPI from '../../services/partnerAPI.js';
import { isLoggedStore } from '../../store/logged.js';

const ROLES = [
  { value: 'employee', label: 'Dipendente', hint: 'Prenotazioni, clienti e valet' },
  { value: 'salesvendor', label: 'Venditore', hint: 'Come il dipendente' },
  { value: 'admin', label: 'Amministratore', hint: 'Come il dipendente, più le Tariffe' },
];

const ROLE_LABELS = {
  employee: 'Dipendente',
  salesvendor: 'Venditore',
  admin: 'Amministratore',
  // dismesso in F1 (migrato ad `admin`): può restare su righe vecchie
  adminEmployee: 'Amministratore',
};

// Stesse voci di `App.vue` nel gestionale partner.
const MENU_BASE = ['Home', 'Agenda', 'Nuova prenotazione', 'Preventivo', 'Clienti'];

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export default {
  name: 'DetailEmployeePage',

  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = isLoggedStore();

    const employee = ref({});
    const partner = ref({});
    const partnerHeadquarters = ref([]);
    const loading = ref(false);
    const loaded = ref(false);
    const loadError = ref(false);
    const editing = ref(false);
    const saving = ref(false);
    const draft = reactive({ headquarterIds: [] });

    // Account e sedi li modifica Simba: per un utente tenant il backend
    // scarterebbe il ruolo, quindi non si fa credere che si possa.
    // Un dipendente eliminato non si modifica più.
    const canEdit = computed(() => partnerAPI.canEditAnagrafica(store.role) && !employee.value.is_deleted);

    const load = async () => {
      loading.value = true;
      loadError.value = false;
      try {
        const res = await partnerAPI.getEmployeeById(route.params.id);
        const body = res.data;
        const e = (Array.isArray(body) ? body[0] : body?.data?.[0]) || null;
        if (!e) throw new Error('Dipendente non trovato');
        employee.value = e;

        const partnerId = e.partnerId || e.headquarters?.[0]?.partner?.id || null;
        partner.value = e.headquarters?.[0]?.partner || (partnerId ? { id: partnerId } : {});
        partnerHeadquarters.value = [];
        if (partnerId) {
          try {
            const p = await partnerAPI.getPartnerById(partnerId);
            const pb = p.data;
            const row = (Array.isArray(pb) ? pb[0] : pb?.data?.[0]) || null;
            if (row) {
              partner.value = row;
              partnerHeadquarters.value = row.headquarters || [];
            }
          } catch (err) {
            console.warn('Sedi del partner non lette:', err?.message);
          }
        }
        loaded.value = true;
      } catch (err) {
        console.error('Caricamento dipendente fallito:', err);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    };

    const assigned = computed(() => employee.value.headquarters || []);
    const fullName = computed(
      () => [employee.value.name, employee.value.surname].filter(Boolean).join(' ') || 'Dipendente',
    );
    const initials = computed(
      () => `${(employee.value.name || '')[0] || ''}${(employee.value.surname || '')[0] || ''}`.toUpperCase() || '?',
    );

    const roleLabel = (r) => ROLE_LABELS[r] || r || '—';
    const menuFor = (r) => (r === 'admin' || r === 'adminEmployee' ? [...MENU_BASE, 'Tariffe'] : MENU_BASE);

    const addressOf = (h) =>
      [
        [h.address, h.street_number].filter(Boolean).join(' '),
        [h.cap, h.city, h.province ? `(${h.province})` : ''].filter(Boolean).join(' '),
      ].filter(Boolean).join(', ') || '—';

    const startEdit = () => {
      Object.assign(draft, {
        name: employee.value.name || '',
        surname: employee.value.surname || '',
        mail: employee.value.mail || '',
        telephone_number: employee.value.telephone_number || '',
        role: employee.value.role === 'adminEmployee' ? 'admin' : employee.value.role || 'employee',
        headquarterIds: assigned.value.map((h) => h.id),
      });
      editing.value = true;
    };
    const cancelEdit = () => {
      editing.value = false;
    };

    const problem = () => {
      if (!draft.name.trim()) return 'Manca il nome';
      if (!draft.surname.trim()) return 'Manca il cognome';
      if (!EMAIL_RE.test(draft.mail.trim())) return 'Email non valida';
      if (!draft.telephone_number.trim()) return 'Manca il telefono';
      if (!draft.headquarterIds.length) return 'Serve almeno una sede';
      return '';
    };

    const save = async () => {
      const p = problem();
      if (p) {
        ElMessage({ message: p, type: 'warning' });
        return;
      }
      saving.value = true;
      try {
        await partnerAPI.updateEmployee({
          id: employee.value.id,
          name: draft.name.trim(),
          surname: draft.surname.trim(),
          mail: draft.mail.trim(),
          telephone_number: draft.telephone_number.trim(),
          role: draft.role,
          // solo gli id: il backend ricava il partner dalle sedi
          headquarters: draft.headquarterIds.map((id) => ({ id })),
        });
        ElMessage({ message: 'Dipendente aggiornato', type: 'success' });
        editing.value = false;
        await load();
      } catch (err) {
        console.error('Salvataggio dipendente fallito:', err);
        const status = err?.response?.status;
        let message = 'Salvataggio non riuscito';
        if (status === 409) message = 'Esiste già un utente con questa email';
        else if (status === 400) message = 'Dati non validi: le sedi devono essere tutte dello stesso partner';
        else if (status === 403) message = 'Non hai i permessi per modificare questo dipendente';
        ElMessage({ message, type: 'error' });
      } finally {
        saving.value = false;
      }
    };

    // Non si cancella la riga: il dipendente non entra più, la sua email si
    // libera e resta nello storico (vedi EmployeeService.elimina).
    const deleting = ref(false);
    const remove = async () => {
      try {
        await ElMessageBox.confirm(
          `${fullName.value} non potrà più entrare nel gestionale e la sua email si potrà usare per un nuovo dipendente. ` +
          'Resta nello storico delle prenotazioni e degli spostamenti che ha gestito.',
          `Eliminare ${fullName.value}?`,
          { confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch (annullato) {
        return;
      }
      deleting.value = true;
      try {
        await partnerAPI.deleteEmployee(employee.value.id);
        ElMessage({ message: `${fullName.value} eliminato`, type: 'success' });
        router.push(partner.value.id ? `/partner/${partner.value.id}` : '/partner');
      } catch (err) {
        // il messaggio del server lo mostra l'interceptor
        console.error('Eliminazione del dipendente fallita:', err?.response?.status);
      } finally {
        deleting.value = false;
      }
    };

    const goBack = () => {
      if (window.history.state?.back) router.back();
      else router.push(partner.value.id ? `/partner/${partner.value.id}` : '/partner');
    };

    onMounted(load);

    return {
      ROLES,
      employee, partner, partnerHeadquarters, assigned,
      loading, loaded, loadError, editing, saving, draft, canEdit,
      fullName, initials, roleLabel, menuFor, addressOf,
      load, startEdit, cancelEdit, save, goBack, deleting, remove,
    };
  },
};
</script>

<style scoped>
.edt { padding: 4px 0 32px; }

/* ---- intestazione ---- */
.edt__head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 18px; }
.edt__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px; cursor: pointer;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.edt__back:hover { border-color: var(--brand-violet); }
.edt__back:focus-visible,
.edt__roleOpt:focus-visible { outline: 2px solid var(--brand-violet); outline-offset: 2px; }
.edt__avatar {
  width: 44px; height: 44px; flex: none; border-radius: 50%; display: grid; place-items: center;
  background: var(--avatar-bg); color: #fff; font-weight: 700; font-size: 15px;
}
.edt__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  color: var(--text-primary);
}
.edt__role {
  font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--text-secondary); background: var(--bg-tertiary); border: 1px solid var(--border-color);
  border-radius: 5px; padding: 1px 7px;
}
.edt__sub { margin-top: 3px; font-size: 12.5px; color: var(--text-tertiary); }
.edt__spacer { flex: 1; }
.edt__actions { display: flex; gap: 8px; flex-wrap: wrap; }

.edt__banner {
  display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding: 10px 13px;
  border-radius: 9px; font-size: 13px; color: var(--state-danger);
  background: var(--state-danger-bg); border: 1px solid var(--state-danger-line);
}

/* ---- griglia ---- */
.edt__grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 16px; align-items: start; }
@media (max-width: 960px) {
  .edt__grid { grid-template-columns: 1fr; }
}
.edt__col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.edt__aside { position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px; }
@media (max-width: 960px) {
  .edt__aside { position: static; }
}

.edt__card { background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: var(--radius-lg); }
.edt__cardHead {
  display: flex; align-items: center; gap: 10px; padding: 13px 16px; border-bottom: 1px solid var(--border-color);
}
.edt__cardHead h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.edt__count { margin-left: auto; font-size: 12px; color: var(--text-tertiary); font-variant-numeric: tabular-nums; }
.edt__cardBody { padding: 16px; }

/* ---- dati ---- */
.edt__fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 22px; }
@media (max-width: 560px) {
  .edt__fields { grid-template-columns: 1fr; }
}
.edt__field { display: flex; flex-direction: column; min-width: 0; }
.edt__field--full { grid-column: 1 / -1; }
.edt__label {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.edt__value { font-size: 14px; font-weight: 600; color: var(--text-primary); overflow-wrap: anywhere; }
.edt__help { margin-top: 5px; font-size: 11.5px; color: var(--text-tertiary); }

.edt__roles { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
@media (max-width: 700px) {
  .edt__roles { grid-template-columns: 1fr; }
}
.edt__roleOpt {
  display: flex; flex-direction: column; align-items: flex-start; gap: 3px; padding: 10px 12px;
  font: inherit; text-align: left; cursor: pointer; border-radius: 9px;
  border: 1.5px solid var(--border-color); background: var(--bg-primary); color: var(--text-primary);
}
.edt__roleOpt:hover { border-color: var(--brand-violet); }
.edt__roleOpt.is-on { border-color: var(--brand-violet); box-shadow: inset 0 0 0 1px var(--brand-violet); }
.edt__roleName { font-size: 13px; font-weight: 700; }
.edt__roleHint { font-size: 12px; color: var(--text-tertiary); }

/* ---- sedi ---- */
.edt__hqList { display: flex; flex-direction: column; gap: 8px; }
.edt__hqCheck { height: auto; align-items: flex-start; margin-right: 0; }
.edt__hqCheck :deep(.el-checkbox__label) { display: flex; flex-direction: column; gap: 2px; white-space: normal; }
.edt__hq {
  display: flex; flex-direction: column; gap: 2px; padding: 10px 12px;
  border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-secondary);
}
.edt__hq + .edt__hq { margin-top: 8px; }
.edt__hqName { font-size: 13.5px; font-weight: 650; color: var(--text-primary); }
.edt__hqAddr { font-size: 12.5px; color: var(--text-secondary); }
.edt__empty { margin: 0; font-size: 13px; color: var(--text-tertiary); }
.edt__warn { margin: 10px 0 0; font-size: 12.5px; font-weight: 600; color: var(--state-attn); }

/* ---- riepilogo ---- */
.edt__kv {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 0;
  border-bottom: 1px solid var(--border-color); font-size: 13px; color: var(--text-secondary);
}
.edt__kv:last-child { border-bottom: 0; }
.edt__kv b { color: var(--text-primary); font-variant-numeric: tabular-nums; text-align: right; }
.edt__link { font-weight: 650; color: var(--brand-violet); text-decoration: none; text-align: right; }
.edt__link:hover { text-decoration: underline; }
.edt__menu { display: flex; flex-wrap: wrap; gap: 6px; }
.edt__menuItem {
  font-size: 12px; font-weight: 600; color: var(--text-secondary); background: var(--bg-tertiary);
  border: 1px solid var(--border-color); border-radius: 20px; padding: 2px 10px;
}
.edt__note { margin: 10px 0 0; font-size: 12px; line-height: 1.5; color: var(--text-tertiary); }
</style>
